import { StorageService } from './StorageService';

// ─── Types ──────────────────────────────────────────

export type DebugLogCategory =
  | 'action'
  | 'state'
  | 'navigation'
  | 'api'
  | 'render';

export interface DebugLogEntry {
  timestamp: number;
  category: DebugLogCategory;
  message: string;
  data?: unknown;
  duration?: number;
}

export interface DebugManagerConfig {
  enabled?: boolean;
  maxLogs?: number;
  namespace?: string;
}

// ─── Constants ──────────────────────────────────────

const DEFAULT_STORAGE_NAMESPACE = 'Debug';
const STORAGE_KEY = 'enabled';
const DEFAULT_MAX_LOGS = 500;

// ─── Class ──────────────────────────────────────────

/**
 * A general-purpose debug logging service.
 * Pure TypeScript — zero framework dependencies. Zero overhead when disabled.
 *
 * Enable via:
 * - URL param: `?debug`
 * - localStorage: `INV_{namespace}_enabled` (persists across tabs)
 * - sessionStorage: `INV_{namespace}_enabled` (per-tab)
 * - Programmatic: `debugManager.enable()`
 *
 * Logs are capped at a configurable limit (default 500) using a circular buffer.
 */
export class DebugManager {
  private enabled: boolean;

  private logs: DebugLogEntry[] = [];

  private readonly maxLogs: number;

  private listeners: Set<() => void> = new Set();

  private sessionStore: StorageService<boolean>;

  private localStore: StorageService<boolean>;

  constructor(config?: DebugManagerConfig) {
    const namespace = config?.namespace ?? DEFAULT_STORAGE_NAMESPACE;

    this.sessionStore = new StorageService<boolean>({
      namespace,
      useSessionStorage: true
    });

    this.localStore = new StorageService<boolean>({
      namespace
    });

    this.maxLogs = config?.maxLogs ?? DEFAULT_MAX_LOGS;
    this.enabled = config?.enabled ?? this.checkEnabled();
  }

  /**
   * Auto-detect debug mode from URL (?debug), localStorage, or sessionStorage.
   * localStorage lets developers enable debug across all tabs,
   * sessionStorage per-tab, and URL param for one-off sessions.
   */
  private checkEnabled(): boolean {
    try {
      return (
        new URLSearchParams(window.location.search).has('debug') ||
        this.localStore.retrieve(STORAGE_KEY) === true ||
        this.sessionStore.retrieve(STORAGE_KEY) === true
      );
    } catch {
      return false;
    }
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Persists to sessionStorage only — debug mode is scoped to the current tab.
   * For persistent cross-tab debugging, set localStorage manually:
   * localStorage.setItem('INV_{namespace}_enabled', 'true')
   */
  enable(): void {
    this.enabled = true;
    this.sessionStore.save(STORAGE_KEY, true);

    this.notify();
  }

  /** Clears the sessionStorage flag. Does not affect localStorage. */
  disable(): void {
    this.enabled = false;
    this.sessionStore.remove(STORAGE_KEY);

    this.notify();
  }

  log(category: DebugLogCategory, message: string, data?: unknown): void {
    if (!this.enabled) return;

    this.logs.push({ timestamp: Date.now(), category, message, data });

    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    this.notify();
  }

  /** Clears all stored log entries and notifies subscribers. */
  clearLogs(): void {
    this.logs = [];
    this.notify();
  }

  getLogs(category?: DebugLogCategory): DebugLogEntry[] {
    if (category)
      return this.logs.filter((entry) => entry.category === category);

    return [...this.logs];
  }

  exportLogs(): string {
    return JSON.stringify(
      {
        exportedAt: new Date().toISOString(),
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        url: typeof window !== 'undefined' ? window.location.href : '',
        logs: this.logs
      },
      null,
      2
    );
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  }

  private notify(): void {
    this.listeners.forEach((listener) => listener());
  }
}
